import React, { useEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import cn from 'classnames';
import { H5 } from '@wheely/ui-kit';

import { Icon } from '../Icon';

import s from './styles.module.css';

type TDocStatus = 'missing' | 'pending' | 'approved' | 'rejected' | 'expired';

interface IDocsListItem {
  docType: string;
  title: string;
  isOptional: boolean;
  status: TDocStatus;
  expiresAt: Date | null;
  expiresSoon: boolean;
  rejectedReason?: string;
}

type HandleDocListItemClick<TDoc extends IDocsListItem = IDocsListItem> = (
  event: any,
  document: TDoc,
) => void;

interface Props<TDoc extends IDocsListItem> {
  docs: TDoc[];
  onChange: HandleDocListItemClick<TDoc>;
  className?: string;
  title?: string;
  isStatusHidden?: boolean;
  scrollToView?: boolean;
}

const isDocExpiring = <TDocItem extends IDocsListItem>(doc: TDocItem) =>
  doc.status === 'approved' && doc.expiresSoon;

const statusesMap: Record<TDocStatus, React.ReactNode> = {
  pending: (
    <FormattedMessage
      defaultMessage="Pending approval"
      id="OLlShR"
      description="Pending document status title"
    />
  ),
  approved: (
    <FormattedMessage
      defaultMessage="Approved"
      id="4suAsB"
      description="Approved document status title"
    />
  ),
  expired: (
    <FormattedMessage
      defaultMessage="Expired"
      id="vG+u3W"
      description="Expired document status title"
    />
  ),
  missing: undefined,
  rejected: (
    <FormattedMessage
      defaultMessage="Rejected"
      id="atQGVL"
      description="Rejected document status title"
    />
  ),
};

const DocStatusLabel = ({ status }: { status: TDocStatus }) => <>{statusesMap[status]}</>;

const DocSubtitle = <TDocItem extends IDocsListItem>({ doc }: { doc: TDocItem }) => {
  const intl = useIntl();

  if (doc.rejectedReason) {
    return <>{doc.rejectedReason}</>;
  }

  if (isDocExpiring(doc) && doc.expiresAt) {
    return (
      <FormattedMessage
        defaultMessage="Expires {date}"
        id="7yQPeq"
        description="Doc subtitle: Expiration date"
        values={{
          date: intl.formatDate(doc.expiresAt, {
            day: 'numeric',
            month: 'short',
          }),
        }}
      />
    );
  }

  return <DocStatusLabel status={doc.status} />;
};

const DocsList = <TDocItem extends IDocsListItem>({
  docs,
  className,
  onChange,
  title,
  isStatusHidden,
  scrollToView = true,
}: Props<TDocItem>) => {
  const intl = useIntl();

  const location = useLocation();
  const nodeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (location.hash === '#docs' && scrollToView) {
      setTimeout(
        () => nodeRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' }),
        500,
      );
    }
  }, [location, scrollToView]);

  const handleClick = (doc: TDocItem) => (event: React.SyntheticEvent) => {
    event.preventDefault();
    onChange(event, doc);
  };

  const docsTotal = docs.length;
  const docsReady = docs.filter(
    doc => doc.status === 'approved' || doc.status === 'pending',
  ).length;

  return (
    <div ref={nodeRef} className={cn(className, s.root)} data-test-id="docs" id="#docs">
      {title && (
        <H5 capitalize={true} className={s.title} data-test-id="docs__header">
          {title}
          <span className={s.count} data-test-id="docs__total">
            {intl.formatMessage(
              {
                defaultMessage: '{docsReady} of {docsTotal}',
                id: 'OKr7TZ',
                description: 'approved docs count',
              },
              {
                docsTotal,
                docsReady,
              },
            )}
          </span>
        </H5>
      )}
      <ul className={s.list}>
        {docs.map(doc => (
          <li key={doc.docType} className={s.item}>
            <button
              type="button"
              className={s.itemButton}
              onClick={handleClick(doc)}
              data-test-id="docs__item"
            >
              <span
                className={cn(s.itemTitle, {
                  [s.hasStatus]: !isStatusHidden,
                  [s.isMissing]: doc.status === 'missing',
                })}
              >
                {doc.title} {doc.isOptional && '*'}
                {!isStatusHidden && doc.status !== 'missing' && (
                  <span
                    data-test-id="docs__item-status"
                    className={cn(s.itemStatus, doc.rejectedReason ? s.rejected : s[doc.status], {
                      [s.expiring]: isDocExpiring(doc),
                    })}
                  >
                    <DocSubtitle<TDocItem> doc={doc} />
                  </span>
                )}
              </span>
              <span className={s.itemIcon}>
                {doc.status === 'missing' ? (
                  <FormattedMessage
                    defaultMessage="ADD"
                    id="AVEk6c"
                    description="Docs list add button text"
                  />
                ) : (
                  <Icon icon="edit-pencil-outline" size="24px" data-test-id="docs__action-icon" />
                )}
              </span>
            </button>
          </li>
        ))}
      </ul>
      {docs.some(doc => doc.isOptional) && (
        <span className={s.tipOptional} data-test-id="docs__note">
          <FormattedMessage
            defaultMessage="* Optional"
            id="vtUs2C"
            description="Docs list optional hint text"
          />
        </span>
      )}
    </div>
  );
};

export type { IDocsListItem, TDocStatus, HandleDocListItemClick };
export { DocsList, DocStatusLabel };
